import produce from 'immer';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import type { Conversation } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { DeepWritable } from 'ts-essentials';
import { put as typedPut } from 'typed-redux-saga';

import { notificationsApi } from 'apis/notifications';
import { numbersApi } from 'apis/numbers';
import { appActions } from 'app/app.actions';
import { conversationsBulkModalActions } from 'app/components/appModals/components/conversationsBulkModal/conversationsBulkModal.actions';
import { timelineActions } from 'components/conversation/actions/timeline';
import { conversationGet } from 'components/conversation/reducers/conversation';
import { feedbackRequest } from 'components/conversation/reducers/feedback';
import { conversationWsQueued } from 'components/conversation/sagas/conversation';
import {
  acwNotificationType,
  getAcwNotificationIdByCsid,
} from 'components/conversation/utils/acwNotification';
import { makeUserMentionedNotificationTitle } from 'components/notifications/components/notificationsPage';
import {
  WebSocketConversationStatusChangedInboundMessage,
  WebSocketMentionUserMentionedInboundMessage,
} from 'middleware/webSocket';
import { transformConversation } from 'utils/conversation/transformConversation';
import { debugLog } from 'utils/debugLog';
import { getShortHumanReadableTimeFromNow } from 'utils/getShortHumanReadableTimeFromNow';
import { pubSub } from 'utils/pubSub';

import { actions as conversationActions } from '../../../../components/conversation/actions/conversation';
import { actions as conversationsActions } from '../../../../components/conversation/actions/conversations';
import {
  typingAdd,
  typingRemove,
} from '../../../../components/conversation/reducers/typing';
import {
  errorConnectionLostShowModal,
  errorReconnectAttempt,
} from '../../../../components/errors/reducers/errors';
import { notificationActions } from '../../../../components/notifications/actions/notification';
import { holdMusicActions } from '../../../../components/powerCallFlow/components/modals/holdMusic/actions/holdMusicActions';
import { waitMusicActions } from '../../../../components/powerCallFlow/components/modals/waitMusic/actions/waitMusicActions';
import { FLOW_ADD_AFTER_BUY_NUMBER } from '../../../../components/powerCallFlow/constants/actionTypes';
import { presenceActions } from '../../../../components/presence/actions/presenceActions';
import * as presenceAPI from '../../../../components/presence/api/presence';
import * as telephonySettingsActions from '../../../../components/settings/actions/telephony';
import { transferToAgentActions } from '../../../../components/shared/transferToAgentModal/actions/transferToAgentModal';
import { transferToNumberActions } from '../../../../components/shared/transferToNumberModal/actions/transferToNumberModal';
import { transferToQueueActions } from '../../../../components/shared/transferToQueueModal/actions/transferToQueueModal';
import { getRequestID } from '../../../../components/shared/util/request';
import { sameCsid } from '../../../../components/telephony/utils/sameCsid';
import { twilioActions } from '../../twilio/actions/twilioActions';
import { websocketActions } from '../actions/websocket';

const {
  conversationsIncreasingUnread,
  conversationsConversationChannelChanged,
} = conversationsActions;

const {
  presenceGetAllSucceeded,
  presenceWsAgentAdded,
  presenceWsAgentStatusChanged,
  presenceWsSetOwnStatus,
} = presenceActions;

const {
  transferToNumberWsResponse,
  transferToNumberWsTransferStarted,
  transferToNumberWsTransferAnswered,
} = transferToNumberActions;

const {
  transferToQueueWsResponse,
  transferToQueueWsTransferStarted,
  transferToQueueWsTransferAnswered,
} = transferToQueueActions;

const {
  transferToAgentWsResponse,
  transferToAgentWsTransferStarted,
  transferToAgentWsTransferAnswered,
} = transferToAgentActions;

const { telephonyBuyGroupNumberDelivered } = telephonySettingsActions.actions;

const {
  notificationAdd,
  notificationDesktopAdd,
  notificationRemoveById,
  notificationFaviconChange,
  notificationSoundPlay,
  notificationTabBadgeShow,
  notificationRemoveByType,
} = notificationActions;

const { presenceSetStatus } = presenceActions;

const { twilioDisconnectCall } = twilioActions;

const { waitMusicWsUploadStatus } = waitMusicActions;

const { holdMusicWsUploadStatus } = holdMusicActions;

const {
  conversationWsClose,
  conversationWsPending,
  conversationWsAwaitingPending,
  conversationWsCancelPending,
  conversationWsOpened,
  conversationAddMessage,
  conversationAnonymizeMessage,
  conversationMergeMessage,
  conversationWsOfferReceived,
  conversationWsOfferCanceled,
  conversationWsTransferOfferReceived,
  conversationAddRating,
  conversationOutboundStarted,
  conversationWsAddAcw,
  conversationWsEmailDeliveryFailure,
  conversationWsLanguageUpdated,
  conversationWsTagged,
  conversationWsUntagged,
  conversationWsAgentAssigned,
  conversationWsAgentUnassigned,
  conversationWsReassignmentStatus,
  conversationWsEndUserPresence,
  conversationWsNewPartyAdded,
  conversationWsNewPartyRemoved,
  conversationWsNewPartyRinging,
  conversationWsNewPartyBusy,
  conversationWsNewPartyFailed,
  conversationWsNewPartyNoAnswer,
  conversationWsMessageDeliveryStatusUpdated,
  conversationWsOutboundMessageStatusUpdated,
  conversationResolutionStatusChange,
} = conversationActions;

/** Returns true if a message is a transcript message. */
const isTranscriptMessage = (msg: any): boolean =>
  !!(
    msg &&
    ((typeof msg.is_transcript === 'string' &&
      msg.is_transcript.toLowerCase() === 'true') ||
      msg.is_transcript === true)
  );

/**
 * Returns true if a message is a redaction message. When redaction, we are
 * creating a new message by copying the original message, modify the text and
 * files, and then we anonymize the original
 */
const isRedactionMessage = (msg: any): boolean =>
  !!(
    msg &&
    ((typeof msg.is_redaction === 'string' &&
      msg.is_redaction.toLowerCase() === 'true') ||
      msg.is_redaction === true)
  );

/** The saga listening in on WebSocket typing events on a conversation. */
export function* wsConversationTypingSaga(): any {
  yield takeLatest(
    (action: any) =>
      action.type === websocketActions.eventReceived.type &&
      action.payload.type === 'conversation:typing',
    wsConversationTypingGen,
  );
}

// Saga
export const websocket = [
  takeEvery(websocketActions.eventReceived.type, wsEventReceivedGen), // for every WS event we look at the type.
  wsConversationTypingSaga(),
  takeLatest(websocketActions.reconnectAttempt.type, wsReconnectWrapperGen), // We watch for reconnectAttempt actions.
  takeLatest(websocketActions.closeReceived.type, wsShouldReconnectGen), // We determine if we should make a reconnect attempt or not.
  takeLatest(websocketActions.reconnectFailed.type, wsShouldReconnectGen),
  takeLatest(
    websocketActions.createFulfilled.type,
    wsSetupPresenceAndTwilioGen,
  ),
];

function* wsConversationTypingGen(action: any) {
  const { settings, typings } = yield select((state: any) => ({
    settings: state.settings,
    typings: state.typing.typings,
  }));

  if (
    // Agent is not the one typing.
    settings.profile.profile.id !== action.payload.params.user_id
  ) {
    if (
      // Typing indicator is not present.
      typings.every((typing: any) => typing.csid !== action.payload.params.csid)
    ) {
      yield put(typingAdd(action.payload));
    }

    yield delay(5000);
    yield put(typingRemove(action.payload));
  }
}

function* wsSetupPresenceAndTwilioGen(action: any) {
  try {
    const getSettings = (state: any) => state.settings;
    const settingsState = yield select(getSettings);
    // We skip presenceGetAll and twilio get Token on init.
    if (action.payload.init === false) {
      const presenceResponse = yield call(
        presenceAPI.presenceGetall as any,
        settingsState.profile.profile.id,
      );
      yield put(
        presenceGetAllSucceeded(
          Object.assign({}, presenceResponse, {
            user_id: settingsState.profile.profile.id,
          }),
        ),
      );
    }
  } catch (exception) {
    console.warn('wsReconnect exception', exception);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: `Reconnect exception: ${exception}`,
      level: 'warning',
    });
    yield put(websocketActions.createRejected());
  }
}

function* wsReconnectWrapperGen() {
  const workerTask = yield fork(wsReconnectGen);
  yield takeLatest(
    websocketActions.cancelReconnect.type,
    cancelWorkerSaga,
    workerTask,
  );
}

function* cancelWorkerSaga(task) {
  yield cancel(task);
  debugLog('WEBSOCKET-MANAGER', 'Reconnect cancelled');
}

function* wsReconnectGen() {
  try {
    const { meta } = yield select((state) => state.errors);

    // We need to find a better solution then delay.
    if (meta.delay > 0) {
      const ms = meta.reconnectAttempt === 1 ? meta.delay + 20000 : meta.delay;

      debugLog(
        'WEBSOCKET-MANAGER',
        `Reconnecting in ${Math.round(ms / 1000)} seconds...`,
      );
      yield delay(ms); // We delay the request.
    }
    yield put(websocketActions.create());
    ampli.websocketConnectionCreated({ source: 'reconnect' });
  } catch (exception) {
    debugLog('WEBSOCKET-MANAGER', 'Reconnect failed', { exception });
    yield put(websocketActions.createRejected());
  }
}

function* wsShouldReconnectGen(action: any) {
  try {
    const getErrors = (state: any) => state.errors;
    const errorsState = yield select(getErrors);
    const { reconnectAttempt } = errorsState.meta;

    if (reconnectAttempt > 1) {
      // Only fire show lost connection modal after we have tried to reconnect first attempt.
      yield put(errorConnectionLostShowModal()); // Error action show modal connection lost fired.
      yield put(notificationFaviconChange({ type: 'offline' })); // update favicon.
    }

    if (reconnectAttempt >= 10) {
      // Stop reconnect attempt after 10 attempts.
      yield put(websocketActions.createRejected());
      debugLog('WEBSOCKET-MANAGER', 'Reconnect aborted after 10 attempts');
    } else {
      yield put(errorReconnectAttempt());
      debugLog('WEBSOCKET-MANAGER', 'Reconnect attempt started');
      ampli.websocketReconnectAttemptStarted({ count: reconnectAttempt });
    }
  } catch (exception) {
    console.warn('WS: closed event received');
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: `Closed event received: ${exception}`,
      level: 'warning',
    });
  }
}

function* conversationAddMessageWithNotificationGen(params: any) {
  try {
    const getVisibility = (state: any) => state.visibility;
    const visibilityState = yield select(getVisibility);
    const playSoundAndUpdateUnread = visibilityState.isHidden === true;
    // The tab is hidden. We should play set some notifications.
    if (playSoundAndUpdateUnread) {
      yield put(notificationTabBadgeShow()); // Show badge - only if tab not is visible.
      yield put(notificationSoundPlay());
      yield put(conversationsIncreasingUnread(params));
    }
    // Add the message to the conversation.
    yield put(conversationAddMessage(params));
  } catch (e) {
    console.warn('conversationAddMessageWithNotificationGen ', e);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: `conversationAddMessageWithNotificationGen: ${e}`,
      level: 'warning',
    });
  }
}

// WS event received.
export function* wsEventReceivedGen(action: any) {
  try {
    if (action.payload.type.startsWith('real_time_dashboard')) {
      pubSub.publish(action.payload.type, action.payload);
    } else if (action.payload.type === 'presence:agent_status_changed') {
      const getSettings = (state) => state.settings;
      const settingsState = yield select(getSettings);
      if (settingsState.profile.profile.id === action.payload.params.user_id) {
        yield put(presenceWsSetOwnStatus(action.payload));
        if (action.payload.params.new_status === 'working') {
          yield put(notificationRemoveByType({ type: 'yourStatusAway' }));
        }
      } else {
        yield put(presenceWsAgentStatusChanged(action.payload));
      }
      pubSub.publish('agentStatusChanged', action.payload);
    } else if (action.payload.type === 'presence:agent_added') {
      yield put(presenceWsAgentAdded(action.payload));
    } else if (action.payload.type === 'member:connection_changed') {
      pubSub.publish('member:connection_changed', action.payload.params);
    } else if (action.payload.type === 'member:availability_changed') {
      pubSub.publish('member:availability_changed', action.payload.params);
    } else if (action.payload.type === 'member:capacity_changed') {
      pubSub.publish('member:capacity_changed', action.payload.params);
    } else if (action.payload.type === 'member:acw_ended') {
      const csid = action.payload.params.csid;
      const agentId = action.payload.params.member_id;
      const authorId = action.payload.params.author_id;
      const getProfile = (state: any) => state.settings.profile.profile;
      const getProfileState = yield select(getProfile);
      const originalExpiresAt = action.payload.params.original_expired_at;
      /**
       * Only show notification if ACW was ended for the current agent and it
       * was an indefinite ACW. For ACW with timeout, we don't need to show an
       * extra notification
       */
      if (
        agentId === getProfileState.id &&
        authorId &&
        agentId != authorId &&
        !originalExpiresAt
      ) {
        const notificationArgs = {
          id: getAcwNotificationIdByCsid(csid),
          type: acwNotificationType,
          title: `Conversation Wrap-up time ended`,
          text: `Wrap-up time ended for`,
          icon: 'done',
          className: 'success',
          time: 5000,
        };
        yield put(notificationAdd(notificationArgs));
      }
    } else if (action.payload.type === 'conversation:offer') {
      const conversationId = action.payload.params.csid;
      const { channel, direction, timeout, offer_type } = action.payload.params;
      ampli.conversationOffered({
        conversationId,
        channel,
        direction,
        timeout,
        offerType: offer_type,
      });
      yield put(conversationWsOfferReceived(action.payload));
    } else if (action.payload.type === 'conversation:transfer_offer') {
      yield put(conversationWsTransferOfferReceived(action.payload));
    } else if (action.payload.type === 'conversation:reassignment_status') {
      if (
        action.payload.params.was_claimed === true &&
        action.payload.params.completed === true
      ) {
        const newAgent = action.payload.params.new_agent;
        const oldAgent = action.payload.params.old_agent;
        const csid = action.payload.params.csid;

        const getProfile = (state: any) => state.settings.profile.profile;
        const getProfileState = yield select(getProfile);

        let isAssignedToMe = false;

        // We need to know if the conversation is assigned to the current agent or not.
        if (newAgent.id === getProfileState.id) {
          isAssignedToMe = true;
        }

        yield put(
          conversationWsReassignmentStatus(
            Object.assign({}, action.payload.params, {
              isAssignedToMe: isAssignedToMe,
            }),
          ),
        ); // Tell conversation reducer that a new agent has been assigned.

        if (getProfileState.id === oldAgent.id) {
          // Was we assigned to the conversation?
          let name = '';
          const text = `You are no longer assigned to #${csid}.`;

          if (newAgent && newAgent.name) {
            name = `by ${newAgent.name}`;
          }

          const option = {
            type: `transfer_${csid}`,
            title: `Conversation claimed ${name}`,
            text: text,
            className: 'warning',
            showDesktop: true,
            mainAction: {
              navigate: `/conversation/${csid}`,
            },
            icon: 'i-info',
          };
          yield put(notificationAdd(option));
        }
      }
    } else if (action.payload.type === 'conversation:transfer_initated') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);

      if (
        metaConversationState.metaConversation.conversation.id ===
        action.payload.params.csid
      ) {
        yield fork(conversationAddMessageWithNotificationGen, {
          message: {
            type: action.payload.type,
            created_at: moment.utc().format(),
            requested_by: action.payload.params.answered_by.id,
          },
          csid: action.payload.params.csid,
          author: action.payload.params.answered_by,
        });
      }
      yield put(transferToQueueWsTransferStarted(action.payload));
      yield put(transferToNumberWsTransferStarted(action.payload));
      yield put(transferToAgentWsTransferStarted(action.payload));
    } else if (action.payload.type === 'conversation:transfer_answered') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      const csid = action.payload.params.csid;

      if (metaConversationState.metaConversation.conversation.id === csid) {
        yield fork(conversationAddMessageWithNotificationGen, {
          message: {
            type: action.payload.type,
            created_at: moment.utc().format(),
            requested_by: action.payload.params.answered_by.id,
          },
          csid: csid,
          author: action.payload.params.answered_by,
        });
      }
      // Transfer to queue.
      yield put(transferToQueueWsTransferAnswered(action.payload));
      yield put(transferToNumberWsTransferAnswered(action.payload));
      if (
        action.payload.params.transfer_type !== 'blind' &&
        action.payload.params.transfer_type !== 'blind_agent'
      ) {
        // We do not want to show complete transfer on blind transfer to agent.
        yield put(transferToAgentWsTransferAnswered(action.payload));
      }
    } else if (action.payload.type === 'conversation:transfer_status') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      const type = action.payload.params.type;
      const csid = action.payload.params.csid;
      const transferee = action.payload.params.transferee; // The receiver of the transfer.
      const cause = action.payload.params.cause;

      let isAssignedToMe = false;

      if (
        action.payload.params.accepted === false &&
        action.payload.params.cause !== 'takeback' &&
        type !== 'assisted_agent'
      ) {
        let name = '';
        let text = `Your transfer on #${csid} was timed out.`;
        if (transferee && transferee.name) {
          name = `to ${transferee.name}`;
        }

        if (cause === 'rejected') {
          text = `Your transfer on #${csid} was rejected.`;
        }

        const option = {
          type: 'transfer_' + csid,
          title: `Transfer could not be delivered ${name}`,
          text: text,
          className: 'warning',
          showDesktop: true,
          mainAction: {
            navigate: `/conversation/${csid}`,
          },
          icon: 'i-conversations', // chat-slash
          time: 30000,
        };
        yield put(notificationAdd(option));
      } else if (
        action.payload.params.accepted === true &&
        type !== 'assisted_agent'
      ) {
        let name = '';
        if (transferee && transferee.name) {
          name = `to ${transferee.name}`;
        }

        // Transfer successfully completed and transfered to another agent.
        const option = {
          type: `transfer_status_${csid}`,
          title: `Conversation transferred ${name}`,
          text: `#${csid} was successfully transferred.`,
          className: 'success',
          showDesktop: true,
          mainAction: {
            navigate: `/conversation/${csid}`,
          },
          icon: 'i-info',
          time: 30000,
        };

        yield put(notificationAdd(option));
      } else if (
        action.payload.params.accepted === false &&
        type === 'assisted_agent' &&
        cause === 'rejected'
      ) {
        let name = '';
        if (transferee.name) {
          name = `to ${transferee.name}`;
        }

        const option = {
          type: `transfer_status_${csid}`,
          title: `Transfer could not be delivered ${name}`,
          text: `Your transfer on #${csid} was rejected.`,
          className: 'success',
          showDesktop: true,
          mainAction: {
            navigate: `/conversation/${csid}`,
          },
          icon: 'i-info',
          time: 30000,
        };

        yield put(notificationAdd(option));
      } else if (
        // The sending agent takes back the assisted transfer.
        action.payload.params.accepted === false &&
        type === 'assisted' &&
        cause === 'takeback'
      ) {
        let name = '';
        if (transferee && transferee.name) {
          name = `to ${transferee.name}`;
        }
        const option = {
          type: `transfer_status_${csid}`,
          title: `Transfer could not be delivered ${name}`,
          text: `You are assigned to conversation #${csid} .`,
          className: 'success',
          showDesktop: true,
          mainAction: {
            navigate: `/conversation/${csid}`,
          },
          icon: 'i-info',
          time: 30000,
        };
        yield put(notificationAdd(option));
      }
      // For change the state of the transfer reducers
      // we use the action transferToAgentWsResponse and transferToNumberWsResponse.
      if (
        action.payload.params.accepted === false ||
        action.payload.params.accepted === true
      ) {
        if (
          type === 'assisted_agent' ||
          type === 'assisted' ||
          type === 'blind_agent'
        ) {
          const getSettings = (state) => state.settings;
          const settingsState = yield select(getSettings);
          if (
            transferee &&
            settingsState.profile.profile.id === transferee.id
          ) {
            // We got the assisted transfer to agent.
            isAssignedToMe = true;
          }
          // We remove the assisted transfer option notfication.
          yield put(
            notificationRemoveByType({
              type: `transfer_assisted_option_${csid}`,
            }),
          );
        }

        yield put(
          appActions.conversationTransferRequest.fulfilled({
            response: action.payload,
          }),
        );

        // These also tells the conversation / conversations reducers that the conversation has been reassigned!
        // Transfer to queue.
        yield put(transferToQueueWsResponse(action.payload.params));
        // Transfer to number.
        yield put(transferToNumberWsResponse(action.payload.params));
        // transfer to agent.
        yield put(
          transferToAgentWsResponse(
            Object.assign({}, action.payload.params, {
              isAssignedToMe: isAssignedToMe,
            }),
          ),
        );
      }

      if (metaConversationState.metaConversation.conversation.id === csid) {
        yield fork(conversationAddMessageWithNotificationGen, {
          message: {
            type: action.payload.type,
            created_at: moment.utc().format(),
            requested_by: action.payload.params.transferee
              ? action.payload.params.transferee.id
              : null,
            resolved_result:
              action.payload.params.accepted === true ? 'true' : 'false',
            target_name: action.payload.params.targetName
              ? action.payload.params.targetName
              : null,
          },
          csid: csid,
          author: action.payload.params.transferee,
        });
      }
    } else if (action.payload.type === 'conversation:closed') {
      let author = null;

      const getConversations = (state: any) => state.conversations;
      const conversationsState = yield select(getConversations);

      const getTelephony = (state) => state.telephony;
      const telephonyState = yield select(getTelephony);

      // We disconnect to any call if we and currently listening to it.
      // `currentCall.csid` is a voice-sdk string while WS `params.csid` is
      // numeric, so normalize both sides before comparing.
      if (
        sameCsid(telephonyState.currentCall?.csid, action.payload.params.csid)
      ) {
        yield put(twilioDisconnectCall({ csid: action.payload.params.csid }));
      }

      for (const metaConversation of conversationsState.metaConversations) {
        // When we first receive the list of conversations from presence on init load
        // The work load does not contain authors object, so we need to test that we
        // are having authors before we start looping though it.
        if (
          metaConversation.conversation.authors &&
          metaConversation.conversation.authors[action.payload.params.closed_by]
        ) {
          author =
            metaConversation.conversation.authors[
              action.payload.params.closed_by
            ];

          yield fork(conversationAddMessageWithNotificationGen, {
            message: {
              type: 'conversation:closed',
              created_at: moment.utc().format(),
              closed_by: action.payload.params.closed_by,
            },
            csid: action.payload.params.csid,
            author: author,
          });
          break;
        }
      }

      // Remove all transfers requests of this conversation.
      // This action is implemented in the new structure.
      // and it will have the same effect as the next 3 actions.
      yield put(
        appActions.conversationTransferClear({
          csid: action.payload.params.csid,
        }),
      );

      // Transfer to queue.
      yield put(
        transferToQueueWsResponse({
          csid: action.payload.params.csid,
          accepted: false,
        }),
      );
      // Transfer to number.
      yield put(
        transferToNumberWsResponse({
          csid: action.payload.params.csid,
          accepted: false,
        }),
      );
      // Transfer to agent.
      yield put(
        transferToAgentWsResponse({
          csid: action.payload.params.csid,
          accepted: false,
        }),
      );

      yield put(conversationWsClose(action.payload));

      yield put(
        notificationRemoveByType({
          type: `conversationOffer_${action.payload.params.csid}`,
        }),
      ); // Remove any offer notifications.
    } else if (action.payload.type === 'conversation:pending') {
      yield put(conversationWsPending(action.payload));
    } else if (action.payload.type === 'conversation:awaiting_pending') {
      yield put(conversationWsAwaitingPending(action.payload));
    } else if (action.payload.type === 'conversation:opened') {
      yield put(conversationWsOpened(action.payload));
    } else if (action.payload.type === 'conversation:pending_cancelled') {
      yield put(conversationWsCancelPending(action.payload));
    } else if (
      action.payload.type === 'conversation:message_delivery_status_updated'
    ) {
      yield put(conversationWsMessageDeliveryStatusUpdated(action.payload));
    } else if (
      action.payload.type === 'conversation:outbound_messages_status_updated'
    ) {
      yield put(conversationWsOutboundMessageStatusUpdated(action.payload));
    } else if (action.payload.type === 'conversation:internal_message_added') {
      const params = action.payload.params;
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);

      if (
        metaConversationState.metaConversation.conversation.id === params.csid
      ) {
        const payload = {
          message: {
            id: params.message_id,
            author_id: params.author_id,
            created_at: moment.utc().format(),
            text: params.message,
            direction: 'inbound',
            type: 'conversation:wrapup_added',
          },
          author: params.author,
          csid: params.csid,
        };
        yield fork(conversationAddMessageWithNotificationGen, payload);
      } else {
        // We are not on the same conversation right now.
        yield put(notificationTabBadgeShow({ force: true })); // Show badge - only if tab not is visible.
        yield put(conversationsIncreasingUnread(params));
      }

      yield put(notificationSoundPlay());

      let text = '';
      let title = `New note on #${params.csid}`;
      if (params && params.message && params.message) {
        text = params.message;
      }
      if (params && params.author && params.author.name) {
        title =
          typeof params.author.name === 'string' &&
          params.author.name.trim().length > 0
            ? params.author.name
            : '';
      }
      const options = {
        title: title,
        time: 5000,
        text: text,
        showDesktop: true,
        type: `new-message-${params.csid}`,
        icon: 'receipt',
        mainAction: {
          navigate: `/conversation/${params.csid}`,
        },
      };
      yield put(notificationDesktopAdd(options));
    } else if (action.payload.type === 'conversation:message_added') {
      const message = action.payload.params.message as any;
      const isTranscript = isTranscriptMessage(message);
      const isRedaction = isRedactionMessage(message);
      // Skip handling of transcript messages altogether
      if (isTranscript && !isRedaction) {
        return;
      }
      pubSub.publish('conversation:message_added', action.payload.params);
      const getSettings = (state: any) => state.settings;
      const settingsState = yield select(getSettings);

      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      const authorId = action.payload.params.author.id;

      if (
        metaConversationState.metaConversation.conversation.id ===
        action.payload.params.csid
      ) {
        // We are on the same conversation right now.
        const isAuthorNotMe = settingsState.profile.profile.id !== authorId;
        const isExternal =
          action.payload.params.message.frontend_id === undefined;
        if (isAuthorNotMe || isExternal) {
          yield fork(
            conversationAddMessageWithNotificationGen,
            action.payload.params,
          );
        } else {
          yield put(conversationMergeMessage(action.payload.params));
        }
      } else {
        // We are not on the same conversation right now.
        yield put(notificationTabBadgeShow({ force: true })); // Show badge - only if tab not is visible.
        yield put(conversationsIncreasingUnread(action.payload.params));
      }
      yield put(notificationSoundPlay());
      yield put(typingRemove(action.payload));

      if (settingsState.profile.profile.id !== authorId) {
        // We are not the author of this message.
        let text = '';
        let title = `New message on #${action.payload.params.csid}`;
        if (
          action.payload.params &&
          action.payload.params.message &&
          action.payload.params.message.text
        ) {
          text = action.payload.params.message.text;
        }
        if (
          action.payload.params &&
          action.payload.params.author &&
          action.payload.params.author.name
        ) {
          title =
            typeof action.payload.params.author.name === 'string' &&
            action.payload.params.author.name.trim().length > 0
              ? action.payload.params.author.name
              : '';
        }
        const options = {
          title: title,
          time: 5000,
          text: text,
          showDesktop: text === '' ? false : true, // Only show desktop notification if there is a message in it.
          type: `new-message-${action.payload.params.csid}`,
          icon: 'chat',
          mainAction: {
            navigate: `/conversation/${action.payload.params.csid}`,
          },
        };
        yield put(notificationDesktopAdd(options));
      }
    } else if (action.payload.type === 'conversation:message_anonymized') {
      yield put(conversationAnonymizeMessage(action.payload.params));
    } else if (action.payload.type === 'conversation:resolved_state_toggled') {
      yield put(
        conversationResolutionStatusChange({
          csid: action.payload.params.csid,
          isResolved: action.payload.params.is_resolved,
          resolvedAt: action.payload.params.resolved_at,
          resolvedBy: action.payload.params.resolved_by,
        }),
      );
    } else if (action.payload.type === 'conversation:typing') {
      // Gets handled separately as it shouldn't be in a `takeEvery`.
    } else if (action.payload.type === 'conversation:rating_added') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);

      if (
        metaConversationState.metaConversation.conversation.id ===
        action.payload.params.csid
      ) {
        yield put(notificationTabBadgeShow()); // Show badge - only if tab not is visible.
        yield put(conversationAddRating(action.payload.params)); // Adds rating to the conversation view.
      } else {
        // Show badge - only if tab not is visible.
        yield put(notificationTabBadgeShow({ force: true })); // Force show badge even if is visible or not.
        yield put(notificationSoundPlay()); // Play the new notification sound.
        yield put(conversationsIncreasingUnread(action.payload.params)); // Increase the unread.
      }

      if (action.payload.params.type === 'thumpsupordown') {
        const options = {
          type: '',
          mainAction: {
            navigate: '/conversation/' + action.payload.params.csid,
          },
          showDesktop: true,
          time: 10000,
          text:
            (action.payload.params.message
              ? action.payload.params.message
              : '') +
            ' (conversation #' +
            action.payload.params.csid +
            ')',
        };
        if (action.payload.params.score === 1) {
          // Thumbs Up
          options.type = 'rating-up';
        } else if (action.payload.params.score === 0) {
          // Thumbs Down
          options.type = 'rating-down';
        }
        yield put(notificationAdd(options));
      }
    } else if (action.payload.type === 'member:acw_started') {
      const params = {
        ...action.payload.params,
        /**
         * The backend returns null here instead of undefined, but the
         * conversationWsAddAcw expect undefined when there is no value, so we
         * need to transform it.
         */
        remaining_duration:
          action.payload.params.remaining_duration ?? undefined,
      };
      yield put(conversationWsAddAcw(params));
    } else if (action.payload.type === 'flow:upload_wait_music_result') {
      pubSub.publish('uploadWaitMusicResult', action.payload);
      yield put(waitMusicWsUploadStatus(action.payload.params));
      yield put(holdMusicWsUploadStatus(action.payload.params));
      if (action.payload.params && !action.payload.params.error) {
        yield put(
          notificationAdd({
            title: 'Your audio file is ready for use',
            text: 'You can now select the new audio file.',
            icon: 'i-info',
            className: 'success',
            showDesktop: true,
          }),
        );
      } else if (action.payload.error && action.payload.error.length > 0) {
        yield put(
          notificationAdd({
            title: 'Error while processing the audio file',
            text: action.payload.error,
            icon: 'i-exclamation',
            className: 'warning',
            showDesktop: true,
          }),
        );
      } else if (
        action.payload.params &&
        action.payload.params.error &&
        action.payload.params.error.length > 0
      ) {
        yield put(
          notificationAdd({
            title: 'Error while processing the audio file',
            text: action.payload.params.error,
            icon: 'i-exclamation',
            className: 'warning',
            showDesktop: true,
          }),
        );
      } else {
        yield put(
          notificationAdd({
            title: 'Error while processing the audio file',
            text: 'An error occurred when trying create your audio file',
            icon: 'i-exclamation',
            className: 'warning',
            showDesktop: true,
          }),
        );
      }
    } else if (
      action.payload.type === 'conversation:agent_assigned' ||
      action.payload.type === 'conversation:agent_unassigned'
    ) {
      const getProfile = (state: any) => state.settings.profile.profile;
      const getProfileState = yield select(getProfile);

      if (action.payload.type === 'conversation:agent_assigned') {
        // We need to know if the conversation is assigned to the current agent or not.
        let isAssignedToMe = false;
        if (getProfileState.id === action.payload.params.agent.id) {
          isAssignedToMe = true;
        }

        yield put(
          conversationWsAgentAssigned(
            Object.assign({}, action.payload.params, {
              isAssignedToMe: isAssignedToMe,
            }),
          ),
        ); // Tell conversation reducer that a new agent has been assigned.
      } else if (action.payload.type === 'conversation:agent_unassigned') {
        // we need to know if the conversation is assigned to the current agent or not.
        let isAssignedToMe: false | null = null;
        if (getProfileState.id === action.payload.params.agent.id) {
          isAssignedToMe = false;
        }
        yield put(
          conversationWsAgentUnassigned(
            Object.assign({}, action.payload.params, {
              isAssignedToMe: isAssignedToMe,
            }),
          ),
        ); // Tell conversation reducer that a agent has been unassigned.
      }

      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      if (
        metaConversationState.metaConversation.conversation.id ===
        action.payload.params.csid
      ) {
        yield fork(conversationAddMessageWithNotificationGen, {
          message: {
            type: action.payload.type,
            created_at: moment.utc().format(),
            requested_by: action.payload.params.agent.id,
          },
          csid: action.payload.params.csid,
          author: action.payload.params.agent,
        });
        yield put(notificationTabBadgeShow()); // Show badge - only if tab not is visible.
      } else {
        yield put(notificationTabBadgeShow({ force: true })); // Force show badge even if is visible or not.
        yield put(notificationSoundPlay()); // Play the new notification sound.
        yield put(conversationsIncreasingUnread(action.payload.params));
      }
      yield put(
        notificationRemoveByType({
          type: `conversationOffer_${action.payload.params.csid}`,
        }),
      ); // Remove any offer notification.
    } else if (action.payload.type === 'conversation:cancel_offer') {
      // Remove any old offer conversation notifications.
      yield put(
        notificationRemoveByType({
          type: `conversationOffer_${action.payload.params.csid}`,
        }),
      );

      yield put(conversationWsOfferCanceled(action.payload));

      if (
        action.payload.params.cause &&
        action.payload.params.cause === 'timeout'
      ) {
        yield put(notificationRemoveByType({ type: 'yourStatusAway' })); // Remove this notification.
        // We add the "Your status is now away because you missed a offer notification".
        const id = getRequestID();
        const option = {
          id: id,
          title: 'Your status is now away',
          text: `Change your status to "working" before you will be offered conversations again.`,
          className: 'warning',
          type: 'yourStatusAway',
          icon: 'i-conversations',
          mainAction: {
            navigate: `/dashboard`,
          },
          showDesktop: true,
          leftAction: {
            text: 'Set to working',
            action: function* () {
              try {
                // Setting status to working will also clear all notifications of this type
                yield put(presenceSetStatus({ status: 'working' }));
              } catch (exception) {
                console.warn(exception);
                Sentry.addBreadcrumb({
                  category: 'websocket',
                  message: `Failed to set status to working: ${exception}`,
                  level: 'warning',
                });
              }
            },
          },
          rightAction: {
            text: 'Dismiss',
            action: function* () {
              yield put(notificationRemoveById({ id: id })); // Remove this notification.
            },
          },
        };
        yield put(notificationAdd(option));
        ampli.notificationOfferTimeout();
      }
    } else if (action.payload.type === 'conversation:outbound_started') {
      yield put(conversationOutboundStarted(action.payload.params)); // An outbound conversation was started.
    } else if (action.payload.type === 'conversation:end_user_presence') {
      yield put(conversationWsEndUserPresence(action.payload));
    } else if (action.payload.type === 'telephony:provisioned_number_status') {
      yield put(
        telephonyBuyGroupNumberDelivered({
          result: {
            data: action.payload.params.number,
            status: action.payload.params.status,
          },
        }),
      );
      yield put(
        numbersApi.util.invalidateTags([{ type: 'Numbers', id: 'LIST' }]),
      );

      // When incoming is verified we add a call flow to the new number.
      // By dispacting the FLOW_ADD_AFTER_BUY_NUMBER action to the call flow saga.
      if (
        action.payload.params.status === 'incoming_functionality' ||
        action.payload.params.status === 'incoming_verified'
      ) {
        yield put({
          type: FLOW_ADD_AFTER_BUY_NUMBER,
          payload: {
            result: {
              data: action.payload.params.number,
            },
          },
        });
      }
    } else if (action.payload.type === 'conversation:status_changed') {
      pubSub.publish(
        'webSocketConversationStatusChanged',
        produce<WebSocketConversationStatusChangedInboundMessage>(
          action.payload,
          (draft) => {
            draft.params.conversation = transformConversation(
              draft.params.conversation,
            ) as DeepWritable<Conversation>;
          },
        ),
      );

      if (
        ['opened', 'closed', 'pending'].includes(action.payload.params.status)
      ) {
        const { conversation } = action.payload.params;
        yield put(timelineActions.timelineUpdateConversation({ conversation }));
      }
    } else if (action.payload.type === 'conversation:email_delivery_failure') {
      yield put(conversationWsEmailDeliveryFailure(action.payload.params));
    } else if (action.payload.type === 'conversation:language_updated') {
      yield put(conversationWsLanguageUpdated(action.payload.params));
    } else if (action.payload.type === 'conversation:tagged') {
      pubSub.publish('conversationTagged', {
        csid: action.payload.params.csid,
        tag: {
          id: action.payload.params.tag.id,
          name: action.payload.params.tag.name,
          color: action.payload.params.tag.color,
          type: 'conversation',
          usage_count: 1,
          is_deactivated: false,
        },
      });
      yield put(conversationWsTagged(action.payload.params));
    } else if (action.payload.type === 'conversation:untagged') {
      pubSub.publish('conversationUntagged', {
        csid: action.payload.params.csid,
        tag: {
          id: action.payload.params.tag.id,
          name: action.payload.params.tag.name,
          color: action.payload.params.tag.color,
          type: 'conversation',
          usage_count: 1,
          is_deactivated: false,
        },
      });
      yield put(conversationWsUntagged(action.payload.params));
    } else if (
      action.payload.type === 'conversation:custom_attributes_updated'
    ) {
      const values = Object.entries(action.payload.params.values).reduce(
        (acc, [key, item]: [string, any]) => {
          acc[key] = {
            data_type:
              item.$type === 'CustomAttributeStringListValue'
                ? 'string_list'
                : 'string',
            value: item.value,
          };
          return acc;
        },
        {},
      );

      pubSub.publish('conversationCustomFieldsUpdated', {
        csid: action.payload.params.csid,
        values: values,
      });
    } else if (action.payload.type === 'conversation:queued') {
      pubSub.publish('conversationQueueUpdated', {
        csid: action.payload.params.csid,
        queue: action.payload.params.queue
          ? {
              id: action.payload.params.queue.id,
              name: action.payload.params.queue.name,
            }
          : null,
      });
      yield put(conversationWsQueued(action.payload.params));
    } else if (
      [
        'conversation:rating_scheduled',
        'conversation:rating_unscheduled',
        'conversation:rating_offered',
        'conversation:rating_cancelled',
        'conversation:rated',
      ].includes(action.payload.type)
    ) {
      yield put(
        conversationAddMessage({
          message: {
            type: action.payload.type,
            created_at: moment.utc().format(),
            ...action.payload.params,
          },
          csid: action.payload.params.csid,
        }),
      );

      // Trigger a request to fetch the feedback for the current conversation. This will provide the data for conversation sidebar.
      if (action.payload.type === 'conversation:rated') {
        yield put(feedbackRequest({ csid: action.payload.params.csid }));
      }
    } else if (action.payload.type === 'conversation:new_party_added') {
      yield put(conversationWsNewPartyAdded(action.payload.params));
    } else if (action.payload.type === 'conversation:new_party_removed') {
      yield put(conversationWsNewPartyRemoved(action.payload.params));
    } else if (action.payload.type === 'conversation:new_party_ringing') {
      yield put(conversationWsNewPartyRinging(action.payload.params));
    } else if (action.payload.type === 'conversation:new_party_busy') {
      yield put(conversationWsNewPartyBusy(action.payload.params));
    } else if (action.payload.type === 'conversation:new_party_failed') {
      yield put(conversationWsNewPartyFailed(action.payload.params));
    } else if (action.payload.type === 'conversation:new_party_no_answer') {
      yield put(conversationWsNewPartyNoAnswer(action.payload.params));
    } else if (action.payload.type === 'bulkaction:started') {
      yield put(conversationsBulkModalActions.startBulkUpdate(action.payload));
    } else if (action.payload.type === 'bulkaction:finished') {
      yield put(conversationsBulkModalActions.finishBulkUpdate(action.payload));
    } else if (action.payload.type === 'conversation:auto_reply_sent') {
      yield fork(conversationAddMessageWithNotificationGen, {
        message: {
          type: action.payload.type,
          created_at: moment.utc().format(),
          ...action.payload.params,
        },
        csid: action.payload.params.csid,
      });
    } else if (action.payload.type === 'conversation:transcription_started') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      const csid = action.payload.params.csid;

      // If viewing the same conversation, show a notification that transcription has started.
      if (metaConversationState?.metaConversation?.conversation?.id === csid) {
        const option = {
          id: `transcription_started_${csid}`,
          type: action.payload.type,
          title: 'Transcription started',
          text: `Transcription has started for conversation #${csid}. You'll be notified when it completes.`,
          className: 'info',
          showDesktop: true,
          icon: 'i-info',
          time: 5000,
        };
        yield put(notificationAdd(option));
      }
    } else if (action.payload.type === 'conversation:transcription_ended') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);

      const csid = action.payload.params.csid;
      const isSuccessful = action.payload.params.is_successful;

      // If viewing the same conversation, refetch the conversation to load and display the
      // transcription messages. If the transcription failed, just show a notification.
      if (metaConversationState?.metaConversation?.conversation?.id === csid) {
        if (isSuccessful) {
          yield put(notificationTabBadgeShow());
          yield put(conversationGet({ csid }));
        } else {
          const failureReason = action.payload.params.failure_reason;

          const option = {
            id: `transcription_ended_${csid}`,
            type: action.payload.type,
            title: 'Transcription failed',
            text: `The transcription for conversation #${csid} has failed${
              failureReason ? `: ${failureReason}` : ''
            }.`,
            className: 'warning',
            showDesktop: true,
            icon: 'i-exclamation',
          };
          yield put(notificationAdd(option));
        }
      } else {
        // If no longer viewing the conversation, show the notification badge, play the notification
        // sound and increase the unread count so the agent is aware that there is something new in
        // the conversation. Navigating to the conversation will load the transcription messages.
        if (isSuccessful) {
          yield put(notificationTabBadgeShow({ force: true }));
          yield put(notificationSoundPlay());
          yield put(conversationsIncreasingUnread(action.payload.params));
        }
      }
    } else if (action.payload.type === 'mention:user_mentioned') {
      const payload: WebSocketMentionUserMentionedInboundMessage =
        action.payload;
      const id = payload.params.id;
      const csid = payload.params.mentioned_on.conversation_id;
      const allUsers = yield select((state) => state.presence.allPresence);

      yield put(
        notificationAdd({
          id,
          type: payload.type,
          title: makeUserMentionedNotificationTitle(
            payload.params.author_id,
            allUsers,
          ),
          // Runs on native desktop notification click.
          mainAction: () => {
            pubSub.publish('navigate', {
              path: '/conversation/:csid',
              params: {
                path: { csid: `${csid}` },
              },
            });
            window.focus();

            ampli.mentionUserNotificationClicked();
          },
          subText: getShortHumanReadableTimeFromNow(Date.now()),
          icon: 'at',
          time: 10000,
          leftAction: {
            text: 'Go to conversation',
            action: function* () {
              yield* typedPut(
                notificationsApi.endpoints.updateNotificationsAsRead.initiate({
                  notificationIds: [id],
                }),
              );

              yield put(notificationRemoveById({ id }));

              pubSub.publish('navigate', {
                path: '/conversation/:csid',
                params: {
                  path: { csid: `${csid}` },
                },
              });

              ampli.mentionUserNotificationClicked();
            },
          },
          rightAction: {
            text: 'Dismiss',
            action: function* () {
              yield put(notificationRemoveById({ id }));
            },
          },
          payloadData: payload,
        }),
      );
    } else if (action.payload.type === 'conversation:channel_changed') {
      const getMetaConversation = (state: any) => state.conversation;
      const metaConversationState = yield select(getMetaConversation);
      const csid = action.payload.params.csid;

      /**
       * If viewing the same conversation, refetch the conversation because some
       * conversation attributes might have changed depending on the channel
       * type. (when going from non email to email)
       */
      if (metaConversationState?.metaConversation?.conversation?.id === csid) {
        yield put(conversationGet({ csid: action.payload.params.csid }));
      }
      // Update the channel in the conversations state for the sidebar
      yield put(conversationsConversationChannelChanged(action.payload.params));
    } else {
      console.warn('WS: unknown ws event received', action);
      Sentry.addBreadcrumb({
        category: 'websocket',
        message: `Unknown ws event received: ${action.payload.type}`,
        level: 'warning',
      });
    }
  } catch (exception) {
    console.warn('WS: event received exception', exception);
    Sentry.addBreadcrumb({
      category: 'websocket',
      message: `Event exception received: ${exception}`,
      level: 'warning',
    });
  }
}
